import React, { memo } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import CustomCard from './CustomCard';
import CustomButton from './CustomButton';
import withRoutingProps from '../../hooks/withRoutingProps';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, hasError: false };
  }

  componentDidMount() {
    window.addEventListener('error', this.handleUncaughtError);
  }

  componentDidCatch(error, errorInfo) {
    if (error) {
      this.logErrorToServer(error, errorInfo); // Optional: Log the error
      this.removeOverlay();

      const relevantStack = errorInfo?.componentStack
        ?.split('\n')
        ?.map(line => line.trim())
        ?.slice(0, 5)
        ?.join('\n');

      this.setState({
        error: error,
        errorInfo: relevantStack,
        hasError: true
      });
    } else {
      this.setState({
        error: null,
        errorInfo: null,
        hasError: false
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleUncaughtError);
  }

  removeOverlay() {
    const overlay = document.getElementById('webpack-dev-server-client-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  handleUncaughtError = event => {
    this.removeOverlay();
    if (event && event.error) {
      console.error('Uncaught Error:', event.error);
    }
  };

  logErrorToServer(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    Sentry.captureException(error, {
      tags: { handler: 'ErrorBoundary' },
      contexts: { react: { componentStack: errorInfo?.componentStack } }
    });
  }

  render() {
    const themeColorObj = this.props.themeColorObj ?? {};

    if (this.state?.hasError) {
      return (
        <CustomCard title={this.props?.cardtitle ? this.props.cardtitle : 'Error'}>
          <span style={{ color: themeColorObj?.primary, fontWeight: '600' }}>
            {this.props?.cardtitle ? this.props.cardtitle : 'Error Occurred'}
          </span>

          {!this.props?.cardtitle && (
            <CustomButton
              title="Refresh"
              size="sm"
              color="primary"
              outline
              className="ml-2"
              onClick={() => window.location.reload()}
            >
              <i className="fa fa-refresh" /> Refresh
            </CustomButton>
          )}

          <div className="w-100 text-center" style={{ overflowWrap: 'anywhere' }}>
            <i
              className="fa fa-exclamation-triangle"
              aria-hidden="true"
              style={{ fontSize: '3rem', color: themeColorObj?.warning }}
            />
            {this.state.hasError && (
              <>
                <p
                  style={{
                    color: themeColorObj?.warning,
                    maxHeight: '6rem',
                    overflowY: 'auto'
                  }}
                >
                  <strong>Error message:</strong>{' '}
                  {this.state?.error?.message || 'An unexpected error occurred.'}
                </p>
                {this.state?.errorInfo && (
                  <pre
                    style={{
                      color: themeColorObj?.warning,
                      whiteSpace: 'pre-wrap',
                      textAlign: 'left',
                      margin: '0 auto',
                      display: 'inline-block'
                    }}
                  >
                    <strong>Component Stack:</strong> {this.state?.errorInfo}
                  </pre>
                )}
              </>
            )}
            <p>{`Oops, something's gone wrong. We are working on resolving the issue.`}</p>
          </div>
        </CustomCard>
      );
    }

    return this.props?.children ?? null;
  }
}

ErrorBoundary.displayName = 'ErrorBoundary';
export default memo(withRoutingProps(ErrorBoundary));

ErrorBoundary.propTypes = {
  children: PropTypes.any,
  themeArr: PropTypes.object,
  cardtitle: PropTypes.string
};
